import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  MoneyInput,
  type MoneyInputProps,
} from "@vega-core/components/Form/Controls/Input/MoneyInput/MoneyInput";
import { Tooltip } from "@vega-core/components/Tooltip/Tooltip";

import { TooltipErrorContent } from "./TooltipErrorContent";

type MoneyInputWithTooltipErrorProps = MoneyInputProps & {
  error?: string;
  tableRef?: RefObject<HTMLDivElement | null>;
};

export const MoneyInputWithTooltipError = ({
  error,
  tableRef,
  ...moneyInputProps
}: MoneyInputWithTooltipErrorProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const hasError = !!error;
  const showTooltip = isFocused && hasError && !isScrolling;
  const containerRef = useRef<HTMLDivElement>(null);
  const internalInputRef = useRef<HTMLInputElement>(null);
  const previousErrorRef = useRef<boolean>(hasError);
  const wasFocusedRef = useRef<boolean>(false);

  useEffect(() => {
    const hadError = previousErrorRef.current;
    const nowHasError = hasError;

    if (hadError !== nowHasError && wasFocusedRef.current) {
      const input = internalInputRef.current;

      if (input) {
        const timeoutId = setTimeout(() => {
          input.focus();
          setIsFocused(true);
        }, 0);

        return () => clearTimeout(timeoutId);
      }
    }

    previousErrorRef.current = nowHasError;
  }, [hasError]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsFocused(false);
      wasFocusedRef.current = false;
    }
  }, []);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    wasFocusedRef.current = true;
    setIsScrolling(false);

    if (tableRef?.current) {
      const handleScroll = () => {
        setIsScrolling(true);
      };
      tableRef.current.addEventListener("scroll", handleScroll, { once: true });
    }
  }, [tableRef]);

  const tooltipContent = hasError ? (
    <TooltipErrorContent error={error} />
  ) : null;

  return (
    <Tooltip
      className="max-w-56"
      content={tooltipContent}
      contentProps={{
        side: "top",
        align: "start",
      }}
      providerProps={{ delayDuration: 0 }}
      rootProps={{ open: showTooltip }}
    >
      <div onBlur={handleBlur} onFocus={handleFocus} ref={containerRef}>
        <MoneyInput
          className={hasError ? "[&>div]:!border-border/negative" : ""}
          ref={internalInputRef}
          {...moneyInputProps}
        />
      </div>
    </Tooltip>
  );
};
